import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEdit, 
  faTrash, 
  faInfoCircle,
  faUser,
  faChevronLeft,
  faTimes,
  faCheck,
  faBan,
  faComment,
  faEye,
  faReply,
  faPaperPlane,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import { 
  doc, 
  updateDoc, 
  collection, 
  addDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  orderBy, 
  where, 
  Timestamp, 
  serverTimestamp, 
  limit, 
  getDoc,
  startAfter,
  arrayUnion,
  onSnapshot,
  runTransaction
} from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import TourContext from '../../context/TourContext';
import UserContext from '../../context/UserContext';
import TopBar from '../TopBar';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import { createNotification } from '../../utils/NotificationService';
import defaultProfilePhoto from '../../assets/Images/defaultUserProfilePicture.png';
import styles from './Tasks.module.css';

// Task Modal Component
const TaskModal = ({ isOpen, onClose, task, onSave, eventMembers, isProcessing }) => {
  const [title, setTitle] = useState(task?.title || '');
  const [details, setDetails] = useState(task?.details || '');
  const [assignedToUserId, setAssignedToUserId] = useState(task?.assignedToUserId || '');
  const [completed, setCompleted] = useState(task?.completed || false);

  useEffect(() => {
    if (task) {
      setTitle(task.title);
      setDetails(task.details);
      setAssignedToUserId(task.assignedToUserId);
      setCompleted(task.completed);
    } else {
      setTitle('');
      setDetails('');
      setAssignedToUserId('');
      setCompleted(false);
    }
  }, [task]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      title,
      details,
      assignedToUserId,
      completed
    });
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>{task ? 'Edit Task' : 'Create New Task'}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <div className={styles.formGroup}>
            <label htmlFor="title">Title</label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              className={styles.input}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="details">Details</label>
            <textarea
              id="details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              className={styles.textarea}
              rows={4}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="assignedTo">Assign To</label>
            <select
              id="assignedTo"
              value={assignedToUserId}
              onChange={(e) => setAssignedToUserId(e.target.value)}
              required
              className={styles.select}
            >
              <option value="">Select User</option>
              {Array.isArray(eventMembers) && eventMembers.map((member) => (
                <option key={member.userId} value={member.userId}>
                  {member.fullName} ({member.userType})
                </option>
              ))}
            </select>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={completed}
                onChange={(e) => setCompleted(e.target.checked)}
                className={styles.checkbox}
              />
              Completed
            </label>
          </div>

          <div className={styles.modalActions}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : (task ? 'Update Task' : 'Create Task')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Rejection Reason Modal Component
const RejectionModal = ({ isOpen, onClose, onSubmit, isProcessing }) => {
  const [reason, setReason] = useState('');
  const [charCount, setCharCount] = useState(0);
  const maxChars = 300;

  const handleReasonChange = (e) => {
    const text = e.target.value;
    if (text.length <= maxChars) {
      setReason(text);
      setCharCount(text.length);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(reason);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>Reject Task</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <div className={styles.formGroup}>
            <label htmlFor="rejectionReason">Reason for Rejection</label>
            <textarea
              id="rejectionReason"
              value={reason}
              onChange={handleReasonChange}
              className={styles.textarea}
              rows={4}
              required
              placeholder="Please provide a reason for rejecting this task..."
            />
            <div className={styles.charCounter}>
              {charCount}/{maxChars} characters
            </div>
          </div>

          <div className={styles.modalActions}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
              disabled={isProcessing}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={isProcessing || reason.trim() === ''}
            >
              {isProcessing ? 'Processing...' : 'Submit Reason'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Response Component
const TaskResponses = ({ task, userData, eventId, dateId, eventType, eventName, onResponseAdded }) => {
  const navigate = useNavigate();
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [showResponses, setShowResponses] = useState(false);
  const [newResponse, setNewResponse] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [lastResponse, setLastResponse] = useState(null);
  const maxChars = 200;
  const responsesPerPage = 3;
  
  const handleResponseChange = (e) => {
    const text = e.target.value;
    if (text.length <= maxChars) {
      setNewResponse(text);
      setCharCount(text.length);
    }
  };
  
  useEffect(() => {
    if (showResponses && task.id) {
      loadResponses();
    }
  }, [showResponses, task.id]);
  
  const loadResponses = async (reset = true) => {
    if (!task.id || !eventId || !dateId) return;
    
    setLoading(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      let responsesQuery;
      
      if (reset) {
        // Initial load
        responsesQuery = query(
          collection(db, collectionName, eventId, 'eventDates', dateId, 'tasks', task.id, 'responses'),
          orderBy('createdAt', 'desc'),
          limit(responsesPerPage)
        );
      } else {
        // Load more
        responsesQuery = query(
          collection(db, collectionName, eventId, 'eventDates', dateId, 'tasks', task.id, 'responses'),
          orderBy('createdAt', 'desc'),
          startAfter(lastResponse),
          limit(responsesPerPage)
        );
      }
      
      const responsesSnapshot = await getDocs(responsesQuery);
      
      if (responsesSnapshot.empty && reset) {
        setResponses([]);
        setHasMore(false);
        setLastResponse(null);
        return;
      }
      
      const responsesList = responsesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setResponses(prev => reset ? responsesList : [...prev, ...responsesList]);
      setHasMore(responsesList.length === responsesPerPage);
      setLastResponse(responsesSnapshot.docs[responsesSnapshot.docs.length - 1] || null);
    } catch (error) {
      console.error('Error loading responses:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleLoadMore = () => {
    if (!loading && hasMore) {
      loadResponses(false);
    }
  };
  
  const handleAddResponse = async () => {
    if (newResponse.trim() === '' || submitting) return;
    
    setSubmitting(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Add response to responses subcollection
      const responseData = {
        text: newResponse.trim(),
        userId: userData.userId,
        userName: userData.userName,
        fullName: userData.fullName,
        profilePictureUrl: userData.profilePictureUrl || '',
        createdAt: serverTimestamp()
      };
      
      const responseRef = await addDoc(
        collection(db, collectionName, eventId, 'eventDates', dateId, 'tasks', task.id, 'responses'),
        responseData
      );
        
      // Update task document to indicate a new response
      // IMPROVED: Now also tracks commenters array using arrayUnion
      await updateDoc(doc(db, collectionName, eventId, 'eventDates', dateId, 'tasks', task.id), {
        lastResponseAt: serverTimestamp(),
        lastResponseBy: userData.userId,
        lastResponseText: newResponse.trim().substring(0, 50) + (newResponse.length > 50 ? '...' : ''),
        responseCount: (task.responseCount || 0) + 1,
        commenters: arrayUnion(userData.userId) // Use arrayUnion to add user ID only if not already present
      });
      
      // Reset response input
      setNewResponse('');
      setCharCount(0);
      
      // Reload responses
      await loadResponses();
      
      // Notify parent component
      if (onResponseAdded) {
        onResponseAdded();
      }
      
      // Send notifications to all involved users with improved function that uses commenters array
      await sendResponseNotifications(task, userData, eventName, eventType, eventId, dateId);
    } catch (error) {
      console.error('Error adding response:', error);
      alert('Failed to add response. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    
    try {
      // Handle Firestore Timestamp
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid date';
    }
  };
  
  return (
    <div className={styles.responsesSection}>
      <div 
        className={styles.responsesSectionHeader}
        onClick={() => setShowResponses(!showResponses)}
      >
        <h4>
          <FontAwesomeIcon icon={faComment} />
          Responses ({task.responseCount || 0})
        </h4>
        <FontAwesomeIcon icon={showResponses ? faChevronUp : faChevronDown} />
      </div>
      
      {showResponses && (
        <div className={styles.responsesSectionContent}>
          <div className={styles.newResponseForm}>
            <textarea
              value={newResponse}
              onChange={handleResponseChange}
              placeholder="Add your response..."
              className={styles.responseInput}
              rows={2}
              maxLength={maxChars}
            />
            <div className={styles.responseActions}>
              <div className={styles.charCounter}>
                {charCount}/{maxChars}
              </div>
              <button
                className={styles.sendButton}
                onClick={handleAddResponse}
                disabled={newResponse.trim() === '' || submitting}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                {submitting ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
          
          {responses.length === 0 ? (
            <div className={styles.noResponses}>
              No responses yet. Be the first to respond!
            </div>
          ) : (
            <div className={styles.responsesList}>
            {responses.map(response => (
  <div key={response.id} className={styles.responseItem}>
    <div className={styles.responseHeader}>
      <div className={styles.responseUser}>
        <div 
          className={styles.responseUserAvatar}
          onClick={() => response.userName ? navigate(`/${response.userName}`) : null}
          title={`View ${response.fullName || response.userName}'s profile`}
        >
          {response.profilePictureUrl ? (
            <img src={response.profilePictureUrl} alt={response.fullName} />
          ) : (
            <img src={defaultProfilePhoto} alt={response.fullName || "User"} />
          )}
        </div>
        <div className={styles.responseUserInfo}>
          <div 
            className={styles.responseUserName}
            onClick={() => response.userName ? navigate(`/${response.userName}`) : null}
          >
            {response.fullName || response.userName}
          </div>
          <div className={styles.responseTime}>
            {formatDate(response.createdAt)}
          </div>
        </div>
      </div>
    </div>
    <div className={styles.responseText}>{response.text}</div>
  </div>
))}
              
              {hasMore && (
                <button
                  className={styles.loadMoreButton}
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load More Responses'}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper function to send notifications for responses
const sendResponseNotifications = async (task, currentUser, eventName, eventType, eventId, dateId) => {
  try {
    console.log('Sending response notifications for task', task.id);
    
    // Create a Set to store unique user IDs (to avoid duplicates)
    const involvedUserIds = new Set();
    
    // ALWAYS include the assigned user and task creator
    if (task.assignedToUserId && task.assignedToUserId !== currentUser.userId) {
      involvedUserIds.add(task.assignedToUserId);
      console.log('Added assigned user to notifications:', task.assignedToUserId);
    }
    
    if (task.assignedByUserId && task.assignedByUserId !== currentUser.userId) {
      involvedUserIds.add(task.assignedByUserId);
      console.log('Added task creator to notifications:', task.assignedByUserId);
    }
    
    // Add all commenters from the task.commenters array
    if (task.commenters && Array.isArray(task.commenters)) {
      task.commenters.forEach(userId => {
        if (userId && userId !== currentUser.userId) {
          involvedUserIds.add(userId);
          console.log('Added previous commenter to notifications:', userId);
        }
      });
    }
    
    // Log the total users to be notified
    console.log(`Sending notifications to ${involvedUserIds.size} users`);
    
    // Create notification message
    const notificationTitle = 'New Response on Task';
    const notificationMessage = `${currentUser.fullName} has responded on a task for ${eventType}: ${eventName}. Please tap to see.`;
    
    // Send notification to each involved user with improved error handling
    const results = await Promise.allSettled(Array.from(involvedUserIds).map(async (userId) => {
      try {
        console.log('Creating notification for user:', userId);
        return await createNotification(userId, 'general', {
          type: 'TASK_RESPONSE',
          title: notificationTitle,
          message: notificationMessage,
          data: {
            eventId,
            eventType,
            dateId,
            taskId: task.id,
            responderId: currentUser.userId,
            _displayData: {
              eventName,
              responderName: currentUser.fullName,
              taskTitle: task.title
            }
          }
        });
      } catch (individualError) {
        console.error(`Error sending notification to user ${userId}:`, individualError);
        return null;
      }
    }));
    
    // Check for any failures
    const successCount = results.filter(result => result.status === 'fulfilled' && result.value).length;
    console.log(`Successfully sent ${successCount} of ${results.length} notifications`);
    
    return successCount > 0;
  } catch (error) {
    console.error('Error sending response notifications:', error);
    return false;
  }
};

// Main Tasks Component
const Tasks = () => {
  const navigate = useNavigate();
  const { eventId, dateId } = useParams();
  const location = useLocation();
  
  // Determine event type from URL path
  const eventType = location.pathname.includes('/tour/') ? 'tour' : 'show';
  
  const { selectedEvent, getSelectedDate, updateDate } = useContext(TourContext);
  const { userData } = useContext(UserContext);
  
  const [tasks, setTasks] = useState([]);
  const [eventMembers, setEventMembers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [rejectingTask, setRejectingTask] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [membersLoading, setMembersLoading] = useState(false);
  const [taskListeners, setTaskListeners] = useState({});

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Clean up listeners on unmount
  useEffect(() => {
    return () => {
      // Unsubscribe from all task listeners
      Object.values(taskListeners).forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
    };
  }, [taskListeners]);

  useEffect(() => {
    const setupForNotificationNavigation = async () => {
      if (!eventId || !dateId) return;
      
      // If coming from a notification, ensure we're loading the correct event
      const searchParams = new URLSearchParams(location.search);
      const fromNotification = searchParams.get('fromNotification') === 'true';
      
      if (fromNotification && (!selectedEvent || selectedEvent.id !== eventId)) {
        console.log('Loading event data from notification navigation');
        
        // Manually load the event data if needed
        try {
          const db = getFirestoreInstance();
          const collectionName = eventType === 'tour' ? 'tours' : 'shows';
          
          // First get the event document
          const eventDoc = await getDoc(doc(db, collectionName, eventId));
          if (eventDoc.exists()) {
            // Get minimal event data needed for this page
            const eventData = eventDoc.data();
            const eventName = eventType === 'show' ? 
              (eventData.showName || eventData.tourName) : 
              eventData.tourName;
            
            console.log(`Successfully loaded ${eventType} data for: ${eventName}`);
            
            // Set minimal data to display while the rest loads
            // We'll keep using this temporary event data until the TourContext catches up
          }
          
          // Also get the date document to ensure it's available
          const dateDoc = await getDoc(doc(db, collectionName, eventId, 'eventDates', dateId));
          if (dateDoc.exists()) {
            console.log('Successfully loaded date data');
          }
        } catch (error) {
          console.error('Error loading event data for notification navigation:', error);
        }
      }
    };
    
    setupForNotificationNavigation();
  }, [eventId, dateId, eventType, location.search, selectedEvent]);


  // Load tasks on component mount or when params change
  useEffect(() => {
    const loadTasks = async () => {
      if (!eventId || !dateId) return;
      
      setIsLoading(true);
      try {
        const db = getFirestoreInstance();
        const collectionName = eventType === 'tour' ? 'tours' : 'shows';
        
        // Clean up existing listeners
        Object.values(taskListeners).forEach(unsubscribe => {
          if (typeof unsubscribe === 'function') {
            unsubscribe();
          }
        });
        setTaskListeners({});
        
        // Set up real-time listener for tasks
        const tasksQuery = query(
          collection(db, collectionName, eventId, 'eventDates', dateId, 'tasks'),
          orderBy('createdAt', 'desc')
        );
        
        const unsubscribe = onSnapshot(tasksQuery, (snapshot) => {
          const tasksList = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          setTasks(tasksList);
          setIsLoading(false);
          
          // Mark as seen by current user
          tasksList.forEach(task => {
            markTaskAsSeen(task);
          });
        }, (error) => {
          console.error('Error in tasks listener:', error);
          setIsLoading(false);
        });
        
        // Store the unsubscribe function
        setTaskListeners(prev => ({
          ...prev,
          main: unsubscribe
        }));
      } catch (error) {
        console.error('Error setting up tasks listener:', error);
        setIsLoading(false);
      }
    };
    
    loadTasks();
    
    // Clean up function
    return () => {
      // Clean up listeners when component unmounts or params change
      Object.values(taskListeners).forEach(unsubscribe => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
      setTaskListeners({});
    };
  }, [eventId, dateId, eventType, userData.userId]);

  // Mark a task as seen by the current user
  const markTaskAsSeen = async (task) => {
    if (!userData?.userId || !task?.id || !eventId || !dateId) return;
    
    // First check if the array exists and has elements
    if (!task.seenBy) task.seenBy = [];
    
    // Then use a more robust check for duplicates by comparing userId directly
    const isDuplicate = Array.isArray(task.seenBy) && 
      task.seenBy.some(user => user.userId === userData.userId);
    
    // Skip if already marked as seen by this user
    if (isDuplicate) {
      console.log('User already marked as seen, skipping');
      return;
    }
    
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      const taskRef = doc(db, collectionName, eventId, 'eventDates', dateId, 'tasks', task.id);
      
      // Create a new user object to add
      const userToAdd = {
        userId: userData.userId,
        userName: userData.userName || '',
        fullName: userData.fullName || '',
        profilePictureUrl: userData.profilePictureUrl || '',
        seenAt: new Date().toISOString() // Use client-side timestamp
      };
      
      // Use a transaction to ensure no race conditions
      await runTransaction(db, async (transaction) => {
        const taskDoc = await transaction.get(taskRef);
        if (!taskDoc.exists()) {
          throw new Error("Task document does not exist!");
        }
        
        const taskData = taskDoc.data();
        const currentSeenBy = taskData.seenBy || [];
        
        // Double check to make sure this user isn't already in the array
        if (!currentSeenBy.some(user => user.userId === userData.userId)) {
          transaction.update(taskRef, {
            seenBy: [...currentSeenBy, userToAdd]
          });
        }
      });
      
      console.log('Successfully marked task as seen');
    } catch (error) {
      console.error('Error marking task as seen:', error);
    }
  };


  // Load event members when modal opens
  useEffect(() => {
    const loadEventMembers = async () => {
      if (!eventId || !isModalOpen) return;
      
      setMembersLoading(true);
      try {
        const db = getFirestoreInstance();
        const collectionName = eventType === 'tour' ? 'tours' : 'shows';
        
        // Query assigned users from the assignedUsers subcollection
        const assignedUsersQuery = query(
          collection(db, collectionName, eventId, 'assignedUsers')
        );
        
        const assignedUsersSnapshot = await getDocs(assignedUsersQuery);
        const membersList = [];
        
        assignedUsersSnapshot.forEach(doc => {
          const userData = doc.data();
          
          // Skip entries that don't have a userId (e.g., bands)
          if (!userData.userId && !doc.id.startsWith('band_')) {
            return;
          }
          
          membersList.push({
            userId: userData.userId || doc.id,
            fullName: userData.fullName || userData.userName || doc.id,
            userType: userData.role || userData.userType || 'Member'
          });
        });
        
        setEventMembers(membersList);
      } catch (error) {
        console.error('Error fetching event members:', error);
        setEventMembers([]);
      } finally {
        setMembersLoading(false);
      }
    };
    
    loadEventMembers();
  }, [eventId, isModalOpen, eventType]);

  const handleCreateTask = () => {
    setEditingTask(null);
    setIsModalOpen(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setIsModalOpen(true);
  };

  const handleBack = () => {
    navigate(`/${eventType}/${eventId}/${dateId}`);
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return dateString;
    }
  };

  const handleSaveTask = async (taskData) => {
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      const eventDate = getSelectedDate();
      
      if (editingTask) {
        // Update existing task
        const taskRef = doc(db, collectionName, eventId, 'eventDates', dateId, 'tasks', editingTask.id);
        
        await updateDoc(taskRef, {
          ...taskData,
          updatedAt: serverTimestamp()
        });
        
        // If assigned user changed, send notification to new assignee
        if (taskData.assignedToUserId !== editingTask.assignedToUserId) {
          // Find the assigned user's name from our members list
          const assignedUser = eventMembers.find(member => member.userId === taskData.assignedToUserId);
          
          // Create notification for the new assigned user
          if (taskData.assignedToUserId !== userData.userId) {
            const eventName = eventType === 'show' ? 
              (selectedEvent?.showName || selectedEvent?.tourName) : 
              selectedEvent?.tourName;
            
            const notificationData = {
              userId: taskData.assignedToUserId,
              eventId,
              eventType,
              dateId,
              taskId: editingTask.id,
              title: 'Task Assigned',
              message: `You have been assigned a task for ${eventType}: ${formatDate(eventDate.date)}. Please tap to respond.`,
              type: 'TASK_ASSIGNED',
              data: {
                eventId,
                eventType,
                dateId,
                taskId: editingTask.id,
                assignedByUserId: userData.userId,
                _displayData: {
                  eventName,
                  taskTitle: taskData.title,
                  eventDate: formatDate(eventDate.date),
                  assignedByName: userData.fullName
                }
              }
            };
            
            await createNotification(taskData.assignedToUserId, 'general', notificationData);
          }
        }
      } else {
        // Create new task
        const taskRef = collection(db, collectionName, eventId, 'eventDates', dateId, 'tasks');
        
        // Find the assigned user's name from our members list
        const assignedUser = eventMembers.find(member => member.userId === taskData.assignedToUserId);
        
        const newTask = {
          ...taskData,
          assignedByUserId: userData.userId,
          assignedByName: userData.fullName,
          assignedToName: assignedUser?.fullName || 'Unknown User',
          notificationIds: [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          status: 'pending', // New task starts as pending until accepted/rejected
          seenBy: [{  // Current user has seen the task
            userId: userData.userId,
            userName: userData.userName || '',
            fullName: userData.fullName || '',
            profilePictureUrl: userData.profilePictureUrl || '',
            seenAt: new Date().toISOString() // Use client-side timestamp instead of serverTimestamp
          }],
          responseCount: 0
        };
        
        const taskDoc = await addDoc(taskRef, newTask);
        
        // Create notification for assigned user
        if (taskData.assignedToUserId !== userData.userId) {
          const eventName = eventType === 'show' ? 
            (selectedEvent?.showName || selectedEvent?.tourName) : 
            selectedEvent?.tourName;
          
          const notificationData = {
            userId: taskData.assignedToUserId,
            eventId,
            eventType,
            dateId,
            taskId: taskDoc.id,
            title: 'Task Assigned',
            message: `You have been assigned a task for ${eventType}: ${formatDate(eventDate.date)}. Please tap to respond.`,
            type: 'TASK_ASSIGNED',
            data: {
              eventId,
              eventType,
              dateId,
              taskId: taskDoc.id,
              assignedByUserId: userData.userId,
              _displayData: {
                eventName,
                taskTitle: taskData.title,
                eventDate: formatDate(eventDate.date),
                assignedByName: userData.fullName
              }
            }
          };
          
          const notificationRef = await createNotification(taskData.assignedToUserId, 'general', notificationData);
          
          // Add notification ID to the task
          if (notificationRef?.id) {
            await updateDoc(doc(db, collectionName, eventId, 'eventDates', dateId, 'tasks', taskDoc.id), {
              notificationIds: [notificationRef.id]
            });
          }
        }
      }
      
      setIsModalOpen(false);
      setEditingTask(null);
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Failed to save task. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteTask = async (taskToDelete) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Delete the responses subcollection first
      const responsesQuery = query(
        collection(db, collectionName, eventId, 'eventDates', dateId, 'tasks', taskToDelete.id, 'responses')
      );
      
      const responsesSnapshot = await getDocs(responsesQuery);
      
      // Use a batch to delete all responses
      const batch = db.batch();
      responsesSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      
      // Delete associated notifications
      if (taskToDelete.notificationIds?.length) {
        for (const notificationId of taskToDelete.notificationIds) {
          try {
            const notificationRef = doc(db, 'users', taskToDelete.assignedToUserId, 'notifications', notificationId);
            batch.delete(notificationRef);
          } catch (error) {
            console.error(`Error deleting notification ${notificationId}:`, error);
            // Continue with other operations even if one fails
          }
        }
      }
      
      // Delete the task
      const taskRef = doc(db, collectionName, eventId, 'eventDates', dateId, 'tasks', taskToDelete.id);
      batch.delete(taskRef);
      
      // Commit all deletions
      await batch.commit();
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleToggleComplete = async (taskToToggle) => {
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Update the task's completed status
      const taskRef = doc(db, collectionName, eventId, 'eventDates', dateId, 'tasks', taskToToggle.id);
      
      await updateDoc(taskRef, {
        completed: !taskToToggle.completed,
        updatedAt: serverTimestamp(),
        completedAt: !taskToToggle.completed ? serverTimestamp() : null,
        completedBy: !taskToToggle.completed ? userData.userId : null
      });
      
      // If the task is being completed, send notification to the task creator
      if (!taskToToggle.completed && taskToToggle.assignedByUserId !== userData.userId) {
        const eventName = eventType === 'show' ? 
          (selectedEvent?.showName || selectedEvent?.tourName) : 
          selectedEvent?.tourName;
        
        const notificationData = {
          userId: taskToToggle.assignedByUserId,
          eventId,
          eventType,
          dateId,
          taskId: taskToToggle.id,
          title: 'Task Completed',
          message: `${userData.fullName} has completed a task for ${eventType}: ${formatDate(getSelectedDate()?.date)}. Please tap to view.`,
          type: 'TASK_COMPLETED',
          data: {
            eventId,
            eventType,
            dateId,
            taskId: taskToToggle.id,
            completedByUserId: userData.userId,
            _displayData: {
              eventName,
              taskTitle: taskToToggle.title,
              completedByName: userData.fullName
            }
          }
        };
        
        await createNotification(taskToToggle.assignedByUserId, 'general', notificationData);
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      alert('Failed to update task status. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAcceptTask = async (taskToAccept) => {
    if (taskToAccept.assignedToUserId !== userData.userId) {
      alert('Only the assigned user can accept this task.');
      return;
    }
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Update the task status
      const taskRef = doc(db, collectionName, eventId, 'eventDates', dateId, 'tasks', taskToAccept.id);
      
      await updateDoc(taskRef, {
        status: 'accepted',
        acceptedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Send notification to task creator
      if (taskToAccept.assignedByUserId !== userData.userId) {
        const eventName = eventType === 'show' ? 
          (selectedEvent?.showName || selectedEvent?.tourName) : 
          selectedEvent?.tourName;
        
        const notificationData = {
          userId: taskToAccept.assignedByUserId,
          eventId,
          eventType,
          dateId,
          taskId: taskToAccept.id,
          title: 'Task Accepted',
          message: `${userData.fullName} has accepted a task for ${eventType}: ${formatDate(getSelectedDate()?.date)}. Please tap to view.`,
          type: 'TASK_ACCEPTED',
          data: {
            eventId,
            eventType,
            dateId,
            taskId: taskToAccept.id,
            acceptedByUserId: userData.userId,
            _displayData: {
              eventName,
              taskTitle: taskToAccept.title,
              acceptedByName: userData.fullName
            }
          }
        };
        
        await createNotification(taskToAccept.assignedByUserId, 'general', notificationData);
      }
    } catch (error) {
      console.error('Error accepting task:', error);
      alert('Failed to accept task. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleShowRejectionModal = (task) => {
    if (task.assignedToUserId !== userData.userId) {
      alert('Only the assigned user can reject this task.');
      return;
    }
    
    setRejectingTask(task);
    setIsRejectionModalOpen(true);
  };

  const handleRejectTask = async (reason) => {
    if (!rejectingTask) return;
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Update the task status
      const taskRef = doc(db, collectionName, eventId, 'eventDates', dateId, 'tasks', rejectingTask.id);
      
      await updateDoc(taskRef, {
        status: 'rejected',
        rejectionReason: reason,
        rejectedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Send notification to task creator
      if (rejectingTask.assignedByUserId !== userData.userId) {
        const eventName = eventType === 'show' ? 
          (selectedEvent?.showName || selectedEvent?.tourName) : 
          selectedEvent?.tourName;
        
        const notificationData = {
          userId: rejectingTask.assignedByUserId,
          eventId,
          eventType,
          dateId,
          taskId: rejectingTask.id,
          title: 'Task Rejected',
          message: `${userData.fullName} has rejected a task for ${eventType}: ${formatDate(getSelectedDate()?.date)}. Please tap to view.`,
          type: 'TASK_REJECTED',
          data: {
            eventId,
            eventType,
            dateId,
            taskId: rejectingTask.id,
            rejectedByUserId: userData.userId,
            _displayData: {
              eventName,
              taskTitle: rejectingTask.title,
              rejectedByName: userData.fullName
            }
          }
        };
        
        await createNotification(rejectingTask.assignedByUserId, 'general', notificationData);
      }
      
      setIsRejectionModalOpen(false);
      setRejectingTask(null);
    } catch (error) {
      console.error('Error rejecting task:', error);
      alert('Failed to reject task. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleResponseAdded = async (taskId) => {
    // This function is called after a response is added to refresh the task if needed
    console.log('Response added to task', taskId);
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading tasks...</p>
      </div>
    );
  }

  const eventName = eventType === 'show' ? 
    (selectedEvent?.showName || selectedEvent?.tourName) : 
    selectedEvent?.tourName;

  if (!selectedEvent || !dateId) {
    return (
      <div className={styles.layout}>
        <main className={styles.main}>
          <div className={styles.emptyState}>
            <FontAwesomeIcon icon={faInfoCircle} size="2x" />
            <p>Please select an event and date to view tasks</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.layout}>
      <div className={styles.mainLayout}>
        {!isMobile && <LeftMenu selectedEvent={selectedEvent} />}
        <main className={styles.content}>
          <div className={styles.header}>
            <button className={styles.backButton} onClick={handleBack}>
              <FontAwesomeIcon icon={faChevronLeft} />
              Back to Schedule
            </button>
            <h1>{eventName}</h1>
            <h2>{formatDate(getSelectedDate()?.date)}</h2>
          </div>

          <div className={styles.taskSection}>
            <button className={styles.createButton} onClick={handleCreateTask}>
              <FontAwesomeIcon icon={faPlus} />
              <span>Create Task</span>
            </button>

            <div className={styles.taskList}>
              {tasks.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>No tasks available</p>
                </div>
              ) : (
                tasks.map(task => (
                  <div key={task.id} className={`${styles.taskCard} ${task.completed ? styles.completed : ''} ${task.status === 'rejected' ? styles.rejected : ''}`}>
                    <div className={styles.taskHeader}>
                      <h3>{task.title}</h3>
                      {task.assignedByUserId === userData.userId && (
                        <div className={styles.taskActions}>
                          <button
                            onClick={() => handleEditTask(task)}
                            className={styles.actionButton}
                            title="Edit Task"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            onClick={() => handleDeleteTask(task)}
                            className={`${styles.actionButton} ${styles.deleteButton}`}
                            title="Delete Task"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      )}
                    </div>

                    <p className={styles.taskDetails}>{task.details}</p>
                    
                    {/* Task Status Section */}
                    {task.status === 'rejected' && (
                      <div className={styles.rejectionSection}>
                        <h4>Rejection Reason:</h4>
                        <p>{task.rejectionReason}</p>
                      </div>
                    )}

                    <div className={styles.taskMeta}>
                      <div className={styles.assignedInfo}>
                        <span className={styles.metaLabel}>
                          <FontAwesomeIcon icon={faUser} /> Assigned To:
                        </span>
                        <span>{task.assignedToName || 'Unknown User'}</span>
                      </div>
                      <div className={styles.assignedInfo}>
                        <span className={styles.metaLabel}>Assigned By:</span>
                        <span>{task.assignedByName || 'Unknown User'}</span>
                      </div>
                    </div>
                    
                    {/* Accept/Reject Buttons - only shown to assigned user for pending tasks */}
                    {task.assignedToUserId === userData.userId && task.status === 'pending' && (
                      <div className={styles.taskActionButtons}>
                        <button 
                          className={styles.acceptButton}
                          onClick={() => handleAcceptTask(task)}
                        >
                          <FontAwesomeIcon icon={faCheck} /> Accept
                        </button>
                        <button 
                          className={styles.rejectButton}
                          onClick={() => handleShowRejectionModal(task)}
                        >
                          <FontAwesomeIcon icon={faBan} /> Reject
                        </button>
                      </div>
                    )}

                    <div className={styles.completedSection}>
                      <label className={styles.checkboxLabel}>
                        <input
                          type="checkbox"
                          checked={task.completed}
                          onChange={() => handleToggleComplete(task)}
                          className={styles.checkbox}
                        />
                        Completed
                      </label>
                    </div>
                    
                    {/* Seen By Section */}
                    {task.seenBy && task.seenBy.length > 0 && (
  <div className={styles.seenBySection}>
    <div className={styles.seenByHeader}>
      <FontAwesomeIcon icon={faEye} />
      <span>Seen by {task.seenBy.length} user{task.seenBy.length !== 1 ? 's' : ''}</span>
    </div>
    <div className={styles.seenByUsers}>
      {task.seenBy.map((user, index) => (
        <div 
          key={user.userId} 
          className={styles.seenByUser}
          onClick={() => user.userName ? navigate(`/${user.userName}`) : null}
          title={`View ${user.fullName || user.userName}'s profile`}
        >
          <div className={styles.seenByUserAvatar}>
            <img 
              src={user.profilePictureUrl || defaultProfilePhoto} 
              alt={user.fullName || user.userName} 
              className={styles.seenByUserAvatarImg}
            />
          </div>
          <span className={styles.seenByUserName}>{user.fullName || user.userName}</span>
        </div>
      ))}
    </div>
  </div>
)}
                    {/* Response Section */}
                    <TaskResponses 
                      task={task} 
                      userData={userData}
                      eventId={eventId}
                      dateId={dateId}
                      eventType={eventType}
                      eventName={eventName}
                      onResponseAdded={() => handleResponseAdded(task.id)}
                    />
                  </div>
                ))
              )}
            </div>
          </div>

          {isModalOpen && (
            <TaskModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              task={editingTask}
              onSave={handleSaveTask}
              eventMembers={eventMembers}
              isProcessing={isProcessing || membersLoading}
            />
          )}
          
          {isRejectionModalOpen && (
            <RejectionModal
              isOpen={isRejectionModalOpen}
              onClose={() => {
                setIsRejectionModalOpen(false);
                setRejectingTask(null);
              }}
              onSubmit={handleRejectTask}
              isProcessing={isProcessing}
            />
          )}

          {isProcessing && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingSpinner}></div>
              <p className={styles.loadingText}>Processing...</p>
            </div>
          )}
        </main>
        {!isMobile && (
          <RightMenu
            selectedEvent={selectedEvent}
            events={[]}
            onEventSelect={() => {}}
          />
        )}
      </div>
    </div>
  );
};

export default Tasks;